/**
 * ---------------------------------------------------------------------------
 * Processor Service Configuration Documentation
 * ---------------------------------------------------------------------------
 *
 * This file is the single source of truth for the NMI processor sign-up
 * "serviceConfig" definitions. Each constant mirrors, verbatim, the
 * serviceConfig payload returned by the gateway/back-end for a given
 * processor. The merchant-account sign-up form (see
 * `NMIMerchantAccountSignUp.tsx`) and the validation model
 * (`NMIProcessorFieldsModel` in `NMIFormModels.ts`) both read from these
 * definitions so that the required fields, validation regexes and helper
 * notes stay in sync with what the processor actually expects.
 *
 * When a processor changes its required fields, update the matching
 * `ServiceConfigDoc` below and the form + validation will follow.
 *
 * IMPORTANT: The `validationRegex` values are stored exactly as the back-end
 * provides them (a stringified JavaScript regex literal, e.g. "/^[0-9]*$/").
 * Use `toRegExp()` to turn one into a usable `RegExp` at runtime.
 * ---------------------------------------------------------------------------
 */

/** A single descriptor sub-rule (descriptor / phone / city / state / url). */
export interface DescriptorRuleDoc {
  isAllowed: boolean;
  maxLength: number;
  validationRegex: string | null;
  helpText: string | null;
}

/** The descriptor block describing what soft-descriptor data is allowed. */
export interface DescriptorDoc {
  descriptor: DescriptorRuleDoc;
  phone: DescriptorRuleDoc;
  city: DescriptorRuleDoc;
  state: DescriptorRuleDoc;
  url: DescriptorRuleDoc;
}

/** Supported field input types inside a serviceConfig. */
export type ProcessorFieldType = "text" | "checkbox" | "select";

/** A single processor field definition as described by the gateway. */
export interface ProcessorFieldDoc {
  /** Back-end field id. Sent back as `fieldId` in the create payload. */
  id: string;
  /** Human readable field name. Sent back as `fieldName`. */
  name: string;
  /** When true the field may be left blank. */
  optional: boolean;
  /** Stringified regex literal (e.g. "/^[0-9]{6}$/") or null for no regex. */
  validationRegex: string | null;
  /** Helper note rendered next to the field. */
  helpText: string;
  type: ProcessorFieldType;
  disabled: boolean;
  readonly: boolean;
  /** When true the field should be visually highlighted (e.g. opt-in flags). */
  highlight: boolean;
  defaultValue: string | boolean;
  selectOptions: Array<{ name: string; value: string }> | null;
  /** When true the stored value should be masked in the UI. */
  masked: boolean;
}

/** Full serviceConfig document for a single processor. */
export interface ServiceConfigDoc {
  objectType: "serviceConfig";
  processorFields: ProcessorFieldDoc[];
  paymentTypes: string[];
  freeTrialAvailable: boolean;
  descriptor: DescriptorDoc;
  allowedCurrencies: string[];
  allowedClassifications: string[];
  defaultCurrency: string;
  singleCurrencyMode: boolean;
  shouldShowMccField: boolean;
  level3Support: boolean;
  checkVerification: boolean;
  avsCvvPreCheckSupport: boolean;
  skipAvsCvvSupport: boolean;
  canBeOffered: boolean;
  serviceLevelRequiredFields: string[];
  faceConfiguration: unknown | null;
  isDisplayOnly: boolean;
}

/* -------------------------------------------------------------------------- */
/* FirstDataNashville                                                          */
/* -------------------------------------------------------------------------- */
export const FIRST_DATA_NASHVILLE_CONFIG: ServiceConfigDoc = {
  objectType: "serviceConfig",
  processorFields: [
    {
      id: "1",
      name: "Merchant ID",
      optional: false,
      validationRegex: "/^[0-9]*$/",
      helpText: "",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "2",
      name: "Terminal ID",
      optional: false,
      validationRegex: "/^[0-9]*$/",
      helpText: "(up to 11 digits)",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "3",
      name: "Ignore Amex CVV Results",
      optional: false,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
    {
      id: "14",
      name: "Disable Purchase Return Authorization functionality for refunds and credits",
      optional: false,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
  ],
  paymentTypes: ["visa", "mastercard", "amex", "discover", "diners", "jcb"],
  freeTrialAvailable: false,
  descriptor: {
    descriptor: {
      isAllowed: false,
      maxLength: 0,
      validationRegex:
        "/^[a-zA-Z0-9\\.\\-'_*+,&$()]{1}[a-zA-Z0-9\\.\\- '_*+,&$()]*$/",
      helpText: "Allowed characters: A-Z, 0-9, and certain special characters.",
    },
    phone: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    city: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    state: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    url: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
  },
  allowedCurrencies: ["USD"],
  allowedClassifications: ["ecommerce", "moto"],
  defaultCurrency: "USD",
  singleCurrencyMode: false,
  shouldShowMccField: true,
  level3Support: false,
  checkVerification: false,
  avsCvvPreCheckSupport: true,
  skipAvsCvvSupport: false,
  canBeOffered: false,
  serviceLevelRequiredFields: [],
  faceConfiguration: null,
  isDisplayOnly: false,
};

/* -------------------------------------------------------------------------- */
/* FirstDataNashvilleNorth                                                     */
/* -------------------------------------------------------------------------- */
export const FIRST_DATA_NASHVILLE_NORTH_CONFIG: ServiceConfigDoc = {
  objectType: "serviceConfig",
  processorFields: [
    {
      id: "1",
      name: "Merchant ID",
      optional: false,
      validationRegex: "/^[0-9]{12}$/",
      helpText: "",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "3",
      name: "Terminal ID",
      optional: false,
      validationRegex: "/^[0-9a-zA-Z]{1,8}$/",
      helpText: "1-8 Chars",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "4",
      name: "Merchant Category Code",
      optional: false,
      validationRegex: "/^[0-9]{4}$/",
      helpText: "",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "7",
      name: "Disable Purchase Return Authorization functionality for refunds and credits",
      optional: false,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
    {
      id: "9",
      name: "Merchant Verification Value",
      optional: true,
      validationRegex: "/^[0-9]{6}$/",
      helpText: "6 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "10",
      name: "Backend MID",
      optional: true,
      validationRegex: "/^[0-9]{0,16}$/",
      helpText:
        "Up to 16 digits; Refers to the backend account for the merchant as a whole.  Shared by all point-of-sale devices/accounts.",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "8",
      name: "Visa Debt Repayment Program",
      optional: true,
      validationRegex: null,
      helpText:
        'All Visa transactions will be submitted as Debt Repayment. The industry classification must be set to "E-Commerce" or "MOTO". Please note: Acquirers may have additional restrictions, such as non-debit cards and specific MCC codes.',
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: true,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
  ],
  paymentTypes: [
    "visa",
    "mastercard",
    "amex",
    "discover",
    "diners",
    "jcb",
    "maestro",
  ],
  freeTrialAvailable: false,
  descriptor: {
    descriptor: {
      isAllowed: true,
      maxLength: 19,
      validationRegex:
        "/^([0-9A-Z \\!\\@\\#\\$\\&\\(\\)\\-'\\.\\/\\+\\,\\\"\\*]{0,19})$/i",
      helpText:
        'Allowed characters: A-Z, 0-9, !, @, #, $, &, slash ("/"), parentheses ("(" and ")"), dash ("-"), plus ("+"), quotes (""" and \'), period ("."), comma, and spaces.',
    },
    phone: {
      isAllowed: true,
      maxLength: 13,
      validationRegex: "/^[()0-9a-zA-Z+\\-., ]*$/",
      helpText: "Please enter a valid phone number.",
    },
    city: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    state: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    url: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
  },
  allowedCurrencies: [
    "AED",
    "AFN",
    "ALL",
    "ANG",
    "ARS",
    "AUD",
    "AWG",
    "AZN",
    "BAM",
    "BBD",
    "BDT",
    "BGN",
    "BHD",
    "BIF",
    "BMD",
    "BND",
    "BOB",
    "BRL",
    "BSD",
    "BWP",
    "BZD",
    "CAD",
    "CDF",
    "CHF",
    "CLP",
    "CNY",
    "COP",
    "CRC",
    "CVE",
    "CZK",
    "DJF",
    "DKK",
    "DOP",
    "DZD",
    "EGP",
    "ERN",
    "ETB",
    "EUR",
    "FJD",
    "FKP",
    "GBP",
    "GEL",
    "GHS",
    "GIP",
    "GMD",
    "GNF",
    "GTQ",
    "GYD",
    "HKD",
    "HNL",
    "HTG",
    "HUF",
    "IDR",
    "ILS",
    "INR",
    "IQD",
    "ISK",
    "JMD",
    "JOD",
    "JPY",
    "KES",
    "KHR",
    "KMF",
    "KPW",
    "KRW",
    "KWD",
    "KYD",
    "KZT",
    "LAK",
    "LBP",
    "LKR",
    "LRD",
    "LSL",
    "MAD",
    "MGA",
    "MKD",
    "MNT",
    "MOP",
    "MRU",
    "MUR",
    "MVR",
    "MWK",
    "MXN",
    "MYR",
    "MZN",
    "NAD",
    "NGN",
    "NIO",
    "NOK",
    "NPR",
    "NZD",
    "OMR",
    "PAB",
    "PEN",
    "PGK",
    "PHP",
    "PKR",
    "PLN",
    "PYG",
    "QAR",
    "RON",
    "RSD",
    "RUB",
    "RWF",
    "SAR",
    "SBD",
    "SCR",
    "SEK",
    "SGD",
    "SHP",
    "SLL",
    "SOS",
    "SRD",
    "STN",
    "SYP",
    "SZL",
    "THB",
    "TJS",
    "TMT",
    "TND",
    "TOP",
    "TRY",
    "TTD",
    "TWD",
    "TZS",
    "UAH",
    "UGX",
    "USD",
    "UYU",
    "VES",
    "VND",
    "VUV",
    "WST",
    "XAF",
    "XCD",
    "XCG",
    "XOF",
    "XPF",
    "YER",
    "ZAR",
    "ZMW",
    "ZWL",
  ],
  allowedClassifications: ["ecommerce", "moto", "retail"],
  defaultCurrency: "USD",
  singleCurrencyMode: false,
  shouldShowMccField: false,
  level3Support: true,
  checkVerification: false,
  avsCvvPreCheckSupport: false,
  skipAvsCvvSupport: false,
  canBeOffered: false,
  serviceLevelRequiredFields: [],
  faceConfiguration: null,
  isDisplayOnly: false,
};

/* -------------------------------------------------------------------------- */
/* ElavonEMV                                                                   */
/* -------------------------------------------------------------------------- */
export const ELAVON_EMV_CONFIG: ServiceConfigDoc = {
  objectType: "serviceConfig",
  processorFields: [
    {
      id: "1",
      name: "Bank Number",
      optional: false,
      validationRegex: "/^[0-9]{6}$/",
      helpText: "6 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "2",
      name: "Terminal ID",
      optional: false,
      validationRegex: "/^[0-9]{0,16}$/",
      helpText: "Up to 16 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "3",
      name: "Merchant ID",
      optional: false,
      validationRegex: "/^[0-9]{0,16}$/",
      helpText: "Up to 16 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
  ],
  paymentTypes: [
    "visa",
    "mastercard",
    "amex",
    "discover",
    "diners",
    "jcb",
    "maestro",
  ],
  freeTrialAvailable: false,
  descriptor: {
    descriptor: {
      isAllowed: true,
      maxLength: 25,
      validationRegex:
        "/^([a-zA-Z\\-\\.\\+\\\\0-9]{1}[a-zA-Z\\-\\.\\+\\ \\\\0-9]{2}\\*[a-zA-Z\\-\\.\\+\\ \\\\0-9]{0,22}|[a-zA-Z\\-\\.\\+\\\\0-9]{1}[a-zA-Z\\-\\.\\+\\ \\\\0-9]{6}\\*[a-zA-Z\\-\\.\\+\\ \\\\0-9]{0,18}|[a-zA-Z\\-\\.\\+\\\\0-9]{1}[a-zA-Z\\-\\.\\+\\ \\\\0-9]{11}\\*[a-zA-Z\\-\\.\\+\\ \\\\0-9]{0,13})$/i",
      helpText:
        'Allowed characters: A-Z, 0-9, period ("."), dash ("-"), plus ("+"), backslash ("\\"), and spaces. There must be a * in the 4th, 8th, or 13th position. Cannot begin with a space.',
    },
    phone: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    city: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    state: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    url: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
  },
  allowedCurrencies: ["CAD", "EUR", "USD"],
  allowedClassifications: ["ecommerce", "retail", "moto"],
  defaultCurrency: "USD",
  singleCurrencyMode: true,
  shouldShowMccField: true,
  level3Support: true,
  checkVerification: false,
  avsCvvPreCheckSupport: false,
  skipAvsCvvSupport: true,
  canBeOffered: false,
  serviceLevelRequiredFields: [],
  faceConfiguration: null,
  isDisplayOnly: false,
};

/* -------------------------------------------------------------------------- */
/* FirstDataNashvilleNorthV2                                                   */
/* -------------------------------------------------------------------------- */
export const FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG: ServiceConfigDoc = {
  objectType: "serviceConfig",
  processorFields: [
    {
      id: "1",
      name: "Merchant ID",
      optional: false,
      validationRegex: "/^[0-9]{12}$/",
      helpText: "12 digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "3",
      name: "Terminal ID",
      optional: false,
      validationRegex: "/^[0-9a-zA-Z]{1,8}$/",
      helpText: "1-8 Chars",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "4",
      name: "Merchant Category Code",
      optional: false,
      validationRegex: "/^[0-9]{4}$/",
      helpText: "4 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "5",
      name: "AMEX Restaurant ID",
      optional: true,
      validationRegex: null,
      helpText: "",
      type: "select",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: [
        { name: "Food And Beverage", value: "0" },
        { name: "Food", value: "1" },
        { name: "Be My Guest", value: "2" },
      ],
      masked: false,
    },
    {
      id: "6",
      name: "Aggregator MID",
      optional: false,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
    {
      id: "7",
      name: "Disable Purchase Return Authorization functionality for refunds and credits",
      optional: false,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
    {
      id: "9",
      name: "Merchant Verification Value",
      optional: true,
      validationRegex: "/^[0-9]{6}$/",
      helpText: "6 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "10",
      name: "Backend MID",
      optional: true,
      validationRegex: "/^[0-9]{0,16}$/",
      helpText:
        "Up to 16 digits; Refers to the backend account for the merchant as a whole.  Shared by all point-of-sale devices/accounts.",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "8",
      name: "Visa Debt Repayment Program",
      optional: true,
      validationRegex: null,
      helpText:
        'All Visa transactions will be submitted as Debt Repayment. The industry classification must be set to "E-Commerce" or "MOTO". Please note: Acquirers may have additional restrictions, such as non-debit cards and specific MCC codes.',
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: true,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
  ],
  paymentTypes: [
    "visa",
    "mastercard",
    "amex",
    "discover",
    "diners",
    "jcb",
    "maestro",
  ],
  freeTrialAvailable: false,
  descriptor: {
    descriptor: {
      isAllowed: true,
      maxLength: 19,
      validationRegex:
        "/^([0-9A-Z \\!\\@\\#\\$\\&\\(\\)\\-'\\.\\/\\+\\,\\\"\\*]{0,19})$/i",
      helpText:
        'Allowed characters: A-Z, 0-9, !, @, #, $, &, slash ("/"), parentheses ("(" and ")"), dash ("-"), plus ("+"), quotes (""" and \'), period ("."), comma, and spaces.',
    },
    phone: {
      isAllowed: true,
      maxLength: 13,
      validationRegex: "/^[()0-9a-zA-Z+\\-., ]*$/",
      helpText: "Please enter a valid phone number.",
    },
    city: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    state: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    url: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
  },
  allowedCurrencies: [
    "AED",
    "AFN",
    "ALL",
    "ANG",
    "ARS",
    "AUD",
    "AWG",
    "AZN",
    "BAM",
    "BBD",
    "BDT",
    "BGN",
    "BHD",
    "BIF",
    "BMD",
    "BND",
    "BOB",
    "BRL",
    "BSD",
    "BWP",
    "BZD",
    "CAD",
    "CDF",
    "CHF",
    "CLP",
    "CNY",
    "COP",
    "CRC",
    "CVE",
    "CZK",
    "DJF",
    "DKK",
    "DOP",
    "DZD",
    "EGP",
    "ERN",
    "ETB",
    "EUR",
    "FJD",
    "FKP",
    "GBP",
    "GEL",
    "GHS",
    "GIP",
    "GMD",
    "GNF",
    "GTQ",
    "GYD",
    "HKD",
    "HNL",
    "HTG",
    "HUF",
    "IDR",
    "ILS",
    "INR",
    "IQD",
    "ISK",
    "JMD",
    "JOD",
    "JPY",
    "KES",
    "KHR",
    "KMF",
    "KPW",
    "KRW",
    "KWD",
    "KYD",
    "KZT",
    "LAK",
    "LBP",
    "LKR",
    "LRD",
    "LSL",
    "MAD",
    "MGA",
    "MKD",
    "MNT",
    "MOP",
    "MRU",
    "MUR",
    "MVR",
    "MWK",
    "MXN",
    "MYR",
    "MZN",
    "NAD",
    "NGN",
    "NIO",
    "NOK",
    "NPR",
    "NZD",
    "OMR",
    "PAB",
    "PEN",
    "PGK",
    "PHP",
    "PKR",
    "PLN",
    "PYG",
    "QAR",
    "RON",
    "RSD",
    "RUB",
    "RWF",
    "SAR",
    "SBD",
    "SCR",
    "SEK",
    "SGD",
    "SHP",
    "SLL",
    "SOS",
    "SRD",
    "STN",
    "SYP",
    "SZL",
    "THB",
    "TJS",
    "TMT",
    "TND",
    "TOP",
    "TRY",
    "TTD",
    "TWD",
    "TZS",
    "UAH",
    "UGX",
    "USD",
    "UYU",
    "VES",
    "VND",
    "VUV",
    "WST",
    "XAF",
    "XCD",
    "XCG",
    "XOF",
    "XPF",
    "YER",
    "ZAR",
    "ZMW",
    "ZWL",
  ],
  allowedClassifications: ["ecommerce", "moto", "retail", "restaurant"],
  defaultCurrency: "USD",
  singleCurrencyMode: false,
  shouldShowMccField: false,
  level3Support: true,
  checkVerification: false,
  avsCvvPreCheckSupport: false,
  skipAvsCvvSupport: false,
  canBeOffered: false,
  serviceLevelRequiredFields: [],
  faceConfiguration: null,
  isDisplayOnly: false,
};

/* -------------------------------------------------------------------------- */
/* GlobalPaymentsEastEMV                                                       */
/* -------------------------------------------------------------------------- */
export const GLOBAL_PAYMENTS_EAST_EMV_CONFIG: ServiceConfigDoc = {
  objectType: "serviceConfig",
  processorFields: [
    {
      id: "1",
      name: "Merchant ID",
      optional: false,
      validationRegex: "/^[0-9]+$/",
      helpText: "",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: true,
    },
    {
      id: "2",
      name: "Bank ID",
      optional: false,
      validationRegex: "/^[0-9]{6}$/",
      helpText: "6 Digits",
      type: "text",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: "",
      selectOptions: null,
      masked: false,
    },
    {
      id: "3",
      name: "Disable Purchase Return Authorization functionality for refunds and credits",
      optional: true,
      validationRegex: null,
      helpText: "",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: false,
      defaultValue: false,
      selectOptions: null,
      masked: false,
    },
    {
      id: "4",
      name: "Merchant is enrolled for surcharging",
      optional: false,
      validationRegex: null,
      helpText:
        "Warning:  Certain rules and regulations may apply when using Convenience Fees/Surcharging based upon various factors. Merchants should be in contact with their merchant account representative to confirm eligibility and compliance.",
      type: "checkbox",
      disabled: false,
      readonly: false,
      highlight: true,
      defaultValue: true,
      selectOptions: null,
      masked: false,
    },
  ],
  paymentTypes: [
    "visa",
    "mastercard",
    "amex",
    "discover",
    "diners",
    "jcb",
    "maestro",
  ],
  freeTrialAvailable: false,
  descriptor: {
    descriptor: {
      isAllowed: false,
      maxLength: 0,
      validationRegex:
        "/^[a-zA-Z0-9\\.\\-'_*+,&$()]{1}[a-zA-Z0-9\\.\\- '_*+,&$()]*$/",
      helpText: "Allowed characters: A-Z, 0-9, and certain special characters.",
    },
    phone: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    city: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    state: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
    url: {
      isAllowed: false,
      maxLength: 0,
      validationRegex: null,
      helpText: null,
    },
  },
  allowedCurrencies: ["USD"],
  allowedClassifications: ["ecommerce", "moto", "retail", "restaurant"],
  defaultCurrency: "USD",
  singleCurrencyMode: false,
  shouldShowMccField: true,
  level3Support: false,
  checkVerification: false,
  avsCvvPreCheckSupport: true,
  skipAvsCvvSupport: true,
  canBeOffered: false,
  serviceLevelRequiredFields: [],
  faceConfiguration: null,
  isDisplayOnly: false,
};

/**
 * Lookup map keyed by the NMI processor identifier. Only processors that are
 * documented here appear in the map.
 */
export const PROCESSOR_SERVICE_CONFIGS: Record<string, ServiceConfigDoc> = {
  FirstDataNashville: FIRST_DATA_NASHVILLE_CONFIG,
  FirstDataNashvilleNorth: FIRST_DATA_NASHVILLE_NORTH_CONFIG,
  ElavonEMV: ELAVON_EMV_CONFIG,
  FirstDataNashvilleNorthV2: FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG,
  GlobalPaymentsEastEMV: GLOBAL_PAYMENTS_EAST_EMV_CONFIG,
};

/**
 * Convert a stringified regex literal (as stored in the serviceConfig, e.g.
 * "/^[0-9]{6}$/" or "/foo/i") into a real `RegExp`. Returns `null` when the
 * input is null/empty so callers can treat "no regex" as "no format check".
 */
export const toRegExp = (pattern: string | null | undefined): RegExp | null => {
  if (!pattern) return null;
  const match = pattern.match(/^\/(.*)\/([a-z]*)$/is);
  if (match) {
    const [, body, flags] = match;
    return new RegExp(body, flags);
  }
  // Fall back to treating the whole string as the pattern body.
  return new RegExp(pattern);
};

/** Find a single field definition inside a serviceConfig by its back-end id. */
export const getFieldDoc = (
  config: ServiceConfigDoc,
  id: string,
): ProcessorFieldDoc | undefined =>
  config.processorFields.find((field) => field.id === id);

/**
 * Resolve the runtime `RegExp` for a field id. Returns `null` when the field
 * has no regex (e.g. checkboxes) or the field/config is missing.
 */
export const getFieldRegExp = (
  config: ServiceConfigDoc,
  id: string,
): RegExp | null => toRegExp(getFieldDoc(config, id)?.validationRegex ?? null);

/** Resolve the helper note for a field id (empty string when absent). */
export const getFieldHelpText = (
  config: ServiceConfigDoc,
  id: string,
): string => getFieldDoc(config, id)?.helpText ?? "";
